import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import {
  useCustomMutation,
  useInvalidate,
  useOne,
  useTranslate,
} from "@refinedev/core";
import { Flex, Form, FormInstance, UploadFile } from "antd";
import PageHeader from "components/PageHeader";
import { ShadowButton } from "components/ShadowButton";
import { useAnalytics } from "hooks/useAnalytics";
import {
  BrandKit,
  ChatMode,
  MediaGenerationMode,
  MediaProjectResponse,
} from "pages/media/types";
import React, {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { AnalyticsEvents } from "types/analytics";
import { ProjectSettingsFormBrandKit } from "./ProjectSettingsFormSteps/ProjectSettingsFormBrandKit";
import { ProjectSettingsFormBrief } from "./ProjectSettingsFormSteps/ProjectSettingsFormBrief";
import { ProjectSettingsFormCallToActions } from "./ProjectSettingsFormSteps/ProjectSettingsFormCallToActions";
import { ProjectSettingsFormContacts } from "./ProjectSettingsFormSteps/ProjectSettingsFormContacts";
import { ProjectSettingsFormDuration } from "./ProjectSettingsFormSteps/ProjectSettingsFormDuration";
import { ProjectSettingsFormExperience } from "./ProjectSettingsFormSteps/ProjectSettingsFormExperience";
import { ProjectSettingsFormLanguage } from "./ProjectSettingsFormSteps/ProjectSettingsFormLanguage";
import { ProjectSettingsFormPreserveDocument } from "./ProjectSettingsFormSteps/ProjectSettingsFormPreserveDocument";
import { ProjectSettingsFormSelectedPages } from "./ProjectSettingsFormSteps/ProjectSettingsFormSelectedPages";
import { ProjectSettingsFormTone } from "./ProjectSettingsFormSteps/ProjectSettingsFormTone";
import { ProjectSettingsFormTransitions } from "./ProjectSettingsFormSteps/ProjectSettingsFormTransitions";
import ToolbarWizard from "./ToolbarWizard";

export type ProjectSettingsItemType =
  | "CallToActions"
  | "Duration"
  | "BrandKit"
  | "Tone"
  | "Brief"
  | "Language"
  | "Contacts"
  | "Disclaimers"
  | "PreserveDocument"
  | "Pages"
  | "Transitions"
  | "Experience";

export function ProjectSettingForm<T>(
  props: Readonly<{
    value: T;
    options?: {
      label: string;
      value: T | undefined;
    }[];
    onClose: () => void;
    title: string;
    subtitle?: ReactNode;
    extra?: ReactNode;
    icon?: ReactNode;
    type: ProjectSettingsItemType;
    projectId: string;
    storyboardId?: string;
    instanceId?: string;
    submitButtonLabel?: ReactNode;
    cancelButtonLabel?: ReactNode;
    onCancel?: () => void;
    cancellable?: boolean;
    onClickContactsButton?: () => void;
    show?: Record<"transitions", boolean>;
    isWizard?: boolean;
    isEdit?: boolean;
    style?: CSSProperties;
    onFinish?: (value: string) => void;
    uploadedFiles?: UploadFile[];
    onBrandKitChanged?: (brandKit: BrandKit) => void;
  }> & {
    formRef?: React.MutableRefObject<{
      form: FormInstance;
      setValue: Dispatch<SetStateAction<T | undefined>>;
    }>;
  }
) {
  const {
    value: initialValue,
    onClose,
    title,
    subtitle,
    extra,
    type,
    projectId,
    storyboardId,
    instanceId,
    submitButtonLabel,
    cancelButtonLabel,
    onCancel,
    cancellable,
    show,
    isWizard,
    isEdit,
    onFinish: _onFinish,
    uploadedFiles,
    formRef,
  } = props;

  const t = useTranslate();
  const [value, setValue] = useState<T | undefined>(initialValue);
  // Captured when type === "Contacts" so we can keep media_generation_mode in
  // sync with the picked contact (no face profile ⇒ audio-only experience).
  const [isVoiceOnly, setIsVoiceOnly] = useState<boolean | null>(null);
  const { mutateAsync, isLoading: isMutateLoading } = useCustomMutation();
  const { trackEvent } = useAnalytics();
  const invalidate = useInvalidate();
  const { data: projectData } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId,
    queryOptions: {
      enabled: !!projectId,
    },
  });

  const locked = projectData?.data
    ? projectData?.data?.settings.status === "Approved" &&
      // allow edit of these settings for storyboard
      ![
        "Tone",
        "Duration",
        "Brief",
        "Language",
        "Pages",
        "PreserveDocument",
        "Contacts",
        "CallToActions",
        "BrandKit",
        "Transitions",
        "Experience",
      ].includes(type)
    : true;
  const isValid = type === "Pages" ? true : value !== null;
  const requestNewMedia = async (
    projectId: string,
    contactIds: string[],
    language: string | undefined
  ) => {
    await mutateAsync({
      url: `media/projects/${projectId}/media`,
      method: "post",
      values: contactIds.map((contactId) => ({
        language,
        contact_id: contactId,
      })),
    });
  };

  const getEndpointUrl = (type: ProjectSettingsItemType): string => {
    const storyboardSettings = [
      "Language",
      "Duration",
      "Tone",
      "Pages",
      "PreserveDocument",
    ];
    const instanceSettings = [
      "CallToActions",
      "BrandKit",
      "Contacts",
      "Transitions",
      "Experience",
    ];

    if (storyboardSettings.includes(type)) {
      if (!storyboardId) {
        throw new Error(`storyboardId is required for ${type} settings`);
      }
      return `media/projects/${projectId}/storyboards/${storyboardId}/settings`;
    } else if (instanceSettings.includes(type)) {
      if (!instanceId) {
        throw new Error(`instanceId is required for ${type} settings`);
      }
      return `media/projects/${projectId}/instances/${instanceId}/settings`;
    } else if (type === "Brief") {
      return `media/projects/${projectId}`;
    }
    throw new Error(`Unknown settings type: ${type}`);
  };

  const handleSave = async () => {
    let values = {};
    const url = getEndpointUrl(type);
    const page = isEdit // to improve
      ? "project_settings"
      : "project_creation_wizard";

    if (type === "Duration") {
      values = { duration: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "movie_duration",
        setting_value: String(value),
        page,
      });
    } else if (type === "CallToActions") {
      values = { cta_group_id: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "call_to_action",
        setting_value: String(value),
        page,
      });
    } else if (type === "Brief") {
      values = { description: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "project_objective",
        setting_value: String(value),
        page,
      });
    } else if (type === "BrandKit") {
      values = { brand_kit_id: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "brandkit",
        setting_value: String(value),
        page,
      });
    } else if (type === "Language") {
      values = { language: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "storyboard_language",
        setting_value: String(value),
        page,
      });
    } else if (type === "Tone") {
      values = { tone_id: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "experience_tone",
        setting_value: String(value),
        page,
      });
    } else if (type === "PreserveDocument") {
      values = { preserve_original_doc: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectData?.data.id ?? "",
        setting_name: "summary_extraction",
        setting_value: value ? "Preserve" : "Extract",
        page,
      });
    } else if (props.type === "Pages") {
      values = { selected_pages: value };
      const formattedValue = value == null ? "" : String(value);
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "selected_pages",
        setting_value: formattedValue,
        page,
      });
    } else if (type === "Transitions") {
      values = { transitions: value };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "video_transition",
        setting_value: String(value),
        page,
      });
    } else if (type === "Experience") {
      const experiences = value as string;

      const media_generation_mode: MediaGenerationMode = experiences.includes(
        "upload"
      )
        ? "Disabled"
        : "Video";
      const chat_mode: ChatMode = experiences.includes("chat")
        ? "FullChat"
        : "Disabled";
      const include_document_animations = experiences.includes("doc");

      values = {
        media_generation_mode,
        chat_mode,
        include_document_animations,
      };
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "experience_type",
        setting_value: experiences,
        page,
      });
    }

    if (type === "Contacts") {
      const language = projectData?.data.settings.language;
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: projectId,
        setting_name: "contact",
        setting_value: JSON.stringify(value),
        page,
      });
      if (language) {
        // when settings are approved, we can make media creation requests
        if (
          projectData.data?.settings.status === "Approved" &&
          Array.isArray(value)
        ) {
          const existingMedia = projectData.data.media ?? [];
          const existingContacts = existingMedia.map((media) => ({
            contactId: media.contact_id,
            language: media.language,
          }));
          const newContacts = value.filter(
            (contactId) =>
              !existingContacts.some(
                (media) =>
                  media.contactId === contactId && media.language === language
              )
          );

          await requestNewMedia(projectId, newContacts as string[], language);
        } else {
          // MED-1044: we can't get contacts from media, so we get them
          // from the select (i.e. we're creating a new project)
          await mutateAsync({
            url,
            method: "patch",
            values: {
              contact_id: value,
              // Keep audio/video mode aligned with the picked contact when known.
              ...(isVoiceOnly !== null && {
                media_generation_mode: isVoiceOnly ? "Audio" : "Video",
              }),
            },
          });
        }
      } else {
        console.error("Language is undefined in projectData.settings");
      }
    } else {
      // other settings
      await mutateAsync({
        url,
        method: "patch",
        values,
      });
    }

    await invalidate({
      resource: "media/projects",
      id: projectId,
      invalidates: ["detail"],
    });

    onClose();
  };

  const handleChange = (value: T) => {
    setValue(value);
  };

  const [form] = Form.useForm();

  useEffect(() => {
    if (formRef && form) {
      formRef.current = {
        form,
        setValue,
      };
    }
  }, [formRef, form]);

  return (
    <Form onFinish={() => void handleSave()} form={form} colon={false}>
      <Flex vertical gap={30} style={{ paddingBottom: 100 }}>
        <Flex style={{ width: "100%" }} align="center" justify="center">
          <PageHeader title={title} subtitle={subtitle as string} />
        </Flex>
        {type === "CallToActions" && (
          <ProjectSettingsFormCallToActions<T>
            value={value}
            onChange={handleChange}
            project={projectData?.data}
          />
        )}
        {type === "Duration" && (
          <ProjectSettingsFormDuration<T>
            value={value}
            onChange={handleChange}
          />
        )}
        {type === "Tone" && (
          <ProjectSettingsFormTone<T> value={value} onChange={handleChange} />
        )}
        {type === "Experience" && (
          <ProjectSettingsFormExperience<T>
            value={value}
            onChange={handleChange}
          />
        )}
        {type === "BrandKit" && (
          <ProjectSettingsFormBrandKit<T>
            project={projectData?.data}
            value={value}
            onChange={handleChange}
            onBrandKitChanged={props.onBrandKitChanged}
            showTransitions={show?.transitions === true}
            isWizard={isWizard}
            instanceId={instanceId}
          />
        )}
        {type === "Transitions" && (
          <ProjectSettingsFormTransitions<T>
            name={"transitions"}
            valuePropName="checked"
            value={value as T}
            initialValue={value}
            onChange={handleChange}
            project={projectData?.data}
          />
        )}
        {type === "Brief" && (
          <ProjectSettingsFormBrief<T> value={value} onChange={handleChange} />
        )}
        {type === "Language" && (
          <ProjectSettingsFormLanguage<T>
            value={value}
            onChange={handleChange}
          />
        )}
        {type === "Contacts" && (
          <ProjectSettingsFormContacts<T>
            project={projectData?.data}
            value={value}
            onChange={handleChange}
            onIsVoiceOnlyChange={setIsVoiceOnly}
            items={[]}
            isSetup={isWizard}
            isEdit={isEdit}
            initialValue={initialValue}
          />
        )}
        {type === "Pages" && (
          <ProjectSettingsFormSelectedPages<T>
            value={value}
            onChange={handleChange}
            uploadedFiles={uploadedFiles ?? []}
          />
        )}
        {type === "PreserveDocument" && (
          <ProjectSettingsFormPreserveDocument<T>
            label={t(
              "projects.components.ProjectSettingForm.contentExtraction"
            )}
            name={"preserve_document"}
            valuePropName="checked"
            value={value as T}
            initialValue={value}
            onChange={handleChange}
          />
        )}
        {extra}
        <ToolbarWizard isWizard={isWizard}>
          {(cancellable ?? true) ? (
            <ShadowButton
              onClick={onCancel ?? onClose}
              type="default"
              shape="round"
              size="large"
              icon={<CaretLeftIcon />}
            >
              {cancelButtonLabel ?? t("buttons.cancel")}
            </ShadowButton>
          ) : (
            <div />
          )}
          {!locked && (
            <ShadowButton
              type="primary"
              shape="round"
              size="large"
              htmlType="submit"
              icon={<CaretRightIcon />}
              iconPosition="end"
              loading={isMutateLoading}
              disabled={isMutateLoading || !isValid}
            >
              {submitButtonLabel ?? t("buttons.save")}
            </ShadowButton>
          )}
        </ToolbarWizard>
      </Flex>
    </Form>
  );
}
